import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getAllArticles, getArticleSources, getRelatedArticles, isIndexable } from '../data/knowledge';
import type { SEOArticle } from '../data/seo-content';
import type { ArticleSource } from '../data/glossary-articles';
import { getPageDates, type PageDates } from '../data/content-dates';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import Byline from '../components/Byline';
import ArticleSources from '../components/ArticleSources';
import NotFound from './NotFound';
import { CheckCircle2 } from 'lucide-react';

// Titles carry SEO suffixes ("... | Voyara AI", "- Travel Tech Glossary")
// that read as noise in a breadcrumb trail.
const shortTitle = (title: string): string =>
  title.split(' | ')[0].replace(/ - Travel Tech Glossary$/, '');

// Every article ends with the two pages that describe the product itself.
const PRODUCT_LINKS = [
  { to: '/travel-intelligence', label: 'How Voyara AI travel intelligence works' },
  { to: '/corporate-travel', label: 'Voyara AI for corporate travel teams' },
];

const toCitation = (source: ArticleSource) => ({
  "@type": "CreativeWork",
  "name": source.title,
  "url": source.url,
  "publisher": { "@type": "Organization", "name": source.publisher }
});

// Robust semantic schema tailored for GEO. A plain function rather than a
// hook: the component returns early for unknown slugs (rules of hooks).
// Only articles in the sitemap have dates; noindexed ones carry none.
// Explainers cite the same sources they list on the page.
function buildSchema(article: SEOArticle, dates: PageDates | undefined, sources: ArticleSource[]) {
  const faqSchema = article.faqs.length > 0 ? {
    "@type": "FAQPage",
    "mainEntity": article.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  const articleSchema = {
    "@type": "Article",
    "@id": `https://www.eclipsegroup.in/knowledge/${article.slug}#article`,
    "headline": article.title,
    "description": article.description,
    "image": "https://www.eclipsegroup.in/og-image.png",
    ...(dates && { "datePublished": dates.published, "dateModified": dates.modified }),
    "author": {
      "@type": "Organization",
      "@id": "https://www.eclipsegroup.in/#organization",
      "name": "Eclipse Group",
      "url": "https://www.eclipsegroup.in"
    },
    "publisher": {
      "@type": "Organization",
      "@id": "https://www.eclipsegroup.in/#organization",
      "name": "Eclipse Group",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.eclipsegroup.in/favicon-512.png",
        "width": 512,
        "height": 512
      }
    },
    "keywords": article.keywords.join(', '),
    ...(sources.length > 0 && { "citation": sources.map(toCitation) }),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.eclipsegroup.in/knowledge/${article.slug}`
    }
  };

  return {
    "@context": "https://schema.org",
    "@graph": [articleSchema, ...(faqSchema ? [faqSchema] : [])]
  };
}

const KnowledgeArticle: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = useMemo(() => getAllArticles().find((a) => a.slug === slug), [slug]);

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles = getRelatedArticles(article.slug);
  const dates = getPageDates(`/knowledge/${article.slug}`);
  const sources = getArticleSources(article.slug);

  const schema = buildSchema(article, dates, sources);

  return (
    <div className="bg-black min-h-screen text-white font-sans selection:bg-blue-500/30">
      <SEO 
        title={article.title}
        description={article.description}
        canonical={`https://www.eclipsegroup.in/knowledge/${article.slug}`}
        schema={schema}
        noIndex={!isIndexable(article.slug)}
      />
      <main className="pt-32 pb-20 global-container max-w-3xl mx-auto px-6">
        <Breadcrumbs
          trail={[
            { name: 'Knowledge Lexicon', path: '/knowledge' },
            { name: shortTitle(article.title), path: `/knowledge/${article.slug}` },
          ]}
        />

        <div className="mb-6">
          <span className="text-blue-400 text-xs font-bold tracking-[0.2em] uppercase bg-blue-500/10 px-3 py-1.5 rounded-md border border-blue-500/20">
            {article.category}
          </span>
        </div>

        <h1 className="font-serif text-[clamp(2.5rem,4vw,3.5rem)] leading-tight mb-8 tracking-tight">
          {article.title}
        </h1>
        {dates && <Byline dates={dates} className="-mt-4 mb-8" />}

        {/* GEO-Optimized Answer Extraction Block */}
        <div className="p-6 rounded-2xl bg-[#0a0a0a] border border-white/10 mb-12 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-blue-500/50" />
          <h2 className="text-xs font-bold tracking-[0.1em] uppercase text-white/50 mb-4 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-blue-400" />
            Executive Summary
          </h2>
          <p className="text-white/90 text-lg leading-relaxed font-medium">
            {article.directAnswer}
          </p>
        </div>

        {/* Article Content */}
        <article className="prose prose-invert prose-lg max-w-none mb-16">
          {article.content.map((paragraph, index) => {
            // Block elements can't sit inside a <p>. Comparison tables come
            // wrapped in a <div> (src/lib/comparisonTable.ts).
            const hasBlockTag = /^(<h[2-6]|<ul|<ol|<blockquote|<div|<table)/i.test(paragraph.trim());
            if (hasBlockTag) {
              return (
                <div 
                  key={index} 
                  dangerouslySetInnerHTML={{ __html: paragraph }} 
                />
              );
            }
            return (
              <p 
                key={index} 
                className="text-secondary leading-relaxed mb-6" 
                dangerouslySetInnerHTML={{ __html: paragraph }} 
              />
            );
          })}
        </article>

        <nav aria-label="Related reading" className="mt-16 pt-12 border-t border-white/10">
          <h2 className="font-serif text-2xl mb-6">Related reading</h2>
          <ul className="space-y-3">
            {[
              ...relatedArticles.map((related) => ({ to: `/knowledge/${related.slug}`, label: shortTitle(related.title) })),
              ...PRODUCT_LINKS,
            ].map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className="text-blue-400 hover:text-blue-300 underline underline-offset-4 decoration-blue-400/30"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* FAQ Section for PAA/GEO */}
        {article.faqs.length > 0 && (
          <div className="mt-16 pt-12 border-t border-white/10">
            <h2 className="font-serif text-2xl mb-8">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {article.faqs.map((faq, index) => (
                <div key={index} className="p-6 rounded-xl bg-white/[0.02] border border-white/5">
                  <h3 className="text-lg font-medium text-white mb-3">{faq.question}</h3>
                  <p className="text-secondary leading-relaxed text-sm">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {sources.length > 0 && <ArticleSources sources={sources} />}

        {/* Semantic Terms (Visually subtle, highly readable for bots) */}
        <div className="mt-16 pt-8 border-t border-white/5">
          <h4 className="text-xs font-bold tracking-[0.1em] uppercase text-white/55 mb-4">Semantic Context Nodes</h4>
          <div className="flex flex-wrap gap-2">
            {article.nlpTerms.map((term, index) => (
              <span key={index} className="text-xs text-white/65 bg-white/[0.02] px-2 py-1 rounded border border-white/[0.05]">
                {term}
              </span>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default KnowledgeArticle;
